import { useMemo, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';

import { cx } from '@/styles/muiTheme';

import { ValidationError, validateSchema, validateValue } from './schemaValidation';

/**
 * Mirrors the server's key pattern exactly.
 *
 * Enforced rather than merely suggested because consumers look these up as
 * hardcoded string literals — a row keyed `tm_url` would be a silent orphan that
 * no code ever reads and no one ever notices.
 */
const KEY_PATTERN = /^[A-Z][A-Z0-9_]*$/;

const DEFAULT_SCHEMA = JSON.stringify(
  {
    type: 'object',
    required: ['enabled'],
    properties: {
      enabled: { type: 'boolean' },
      limit: { type: 'integer', minimum: 0, maximum: 100 },
    },
  },
  null,
  2,
);

const DEFAULT_VALUE = JSON.stringify({ enabled: true, limit: 10 }, null, 2);

interface CreateConfigFormProps {
  /** Keys already in the registry, for the availability check. */
  existingKeys: string[];
  requireReason: boolean;
  isSaving: boolean;
  serverError: string | null;
  onCancel: () => void;
  onCreate: (input: { key: string; value: string; schema: unknown; note: string | null }) => void;
}

const ErrorList = ({ errors }: { errors: ValidationError[] }) => (
  <Stack sx={{ gap: 0.25, marginTop: 0.5 }}>
    {errors.map((error) => (
      <Typography
        key={`${error.path}:${error.message}`}
        variant="caption"
        sx={{ color: cx.red.main }}
      >
        <Box component="span" sx={{ fontFamily: cx.fontMono }}>
          {error.path}
        </Box>{' '}
        — {error.message}
      </Typography>
    ))}
  </Stack>
);

/**
 * The create form.
 *
 * Schema first, then value, then a check of one against the other — the same
 * order the server runs. Getting a live answer to "does my seed value satisfy
 * the contract I just wrote?" before the row exists is the point: a config
 * created with a schema its own value violates is immediately un-editable in the
 * form, because every save would fail validation.
 */
const CreateConfigForm = ({
  existingKeys,
  requireReason,
  isSaving,
  serverError,
  onCancel,
  onCreate,
}: CreateConfigFormProps) => {
  const [key, setKey] = useState('');
  const [note, setNote] = useState('');
  const [schemaText, setSchemaText] = useState(DEFAULT_SCHEMA);
  const [valueText, setValueText] = useState(DEFAULT_VALUE);

  const { schema, schemaErrors } = useMemo(() => {
    try {
      const parsed = JSON.parse(schemaText);
      return { schema: parsed, schemaErrors: validateSchema(parsed) };
    } catch (error) {
      return {
        schema: null,
        schemaErrors: [{ path: '$', message: `Invalid JSON: ${(error as Error).message}` }],
      };
    }
  }, [schemaText]);

  const valueErrors = useMemo(() => {
    try {
      const parsed = JSON.parse(valueText);
      // Only checked against the schema once the schema itself is sound —
      // otherwise the value panel fills with errors that are really the
      // schema's fault.
      return schema && !schemaErrors.length ? validateValue(parsed, schema) : [];
    } catch (error) {
      return [{ path: '$', message: `Invalid JSON: ${(error as Error).message}` }];
    }
  }, [valueText, schema, schemaErrors]);

  const keyTaken = existingKeys.includes(key);
  const keyValid = KEY_PATTERN.test(key) && !keyTaken;

  const keyHint = (() => {
    if (!key) return 'UPPER_SNAKE_CASE — letters, digits and underscores';
    if (keyTaken) return 'A config with this key already exists';
    if (!KEY_PATTERN.test(key)) return 'Must start with a letter; only A–Z, 0–9 and _';
    return 'Key is available';
  })();

  const canCreate =
    keyValid &&
    !schemaErrors.length &&
    !valueErrors.length &&
    (!requireReason || !!note.trim()) &&
    !isSaving;

  return (
    <Stack sx={{ gap: 2.5 }}>
      <Typography variant="body2" sx={{ color: cx.text.secondary }}>
        New config
      </Typography>

      {serverError && <Alert severity="error">{serverError}</Alert>}

      <Box>
        <TextField
          size="small"
          label="Key"
          value={key}
          // Sanitising on the way in rather than validating on the way out: the
          // field can only ever hold a legal key, so the hint below is about
          // availability, not about characters the operator cannot type anyway.
          onChange={(event) => setKey(event.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_'))}
          sx={{
            width: 360,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: !key ? cx.border.strong : keyValid ? cx.green : cx.red.main,
            },
          }}
        />
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            marginTop: 0.5,
            color: !key ? cx.text.muted : keyValid ? cx.green : cx.red.main,
          }}
        >
          {keyHint}
        </Typography>
      </Box>

      <Box>
        <Typography variant="body2" sx={{ color: cx.text.secondary, marginBottom: 0.75 }}>
          JSON Schema
        </Typography>
        <TextField
          multiline
          minRows={10}
          fullWidth
          value={schemaText}
          onChange={(event) => setSchemaText(event.target.value)}
          sx={{
            '& textarea': { fontFamily: cx.fontMono, fontSize: 12 },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: schemaErrors.length ? cx.red.main : cx.border.strong,
            },
          }}
        />
        {schemaErrors.length ? (
          <ErrorList errors={schemaErrors} />
        ) : (
          <Typography variant="caption" sx={{ color: cx.text.muted }}>
            Defines the shape the value must satisfy, now and on every future edit.
          </Typography>
        )}
      </Box>

      <Box>
        <Typography variant="body2" sx={{ color: cx.text.secondary, marginBottom: 0.75 }}>
          Initial value
        </Typography>
        <TextField
          multiline
          minRows={8}
          fullWidth
          value={valueText}
          onChange={(event) => setValueText(event.target.value)}
          sx={{
            '& textarea': { fontFamily: cx.fontMono, fontSize: 12 },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: valueErrors.length ? cx.red.main : cx.border.strong,
            },
          }}
        />
        {valueErrors.length ? (
          <ErrorList errors={valueErrors} />
        ) : (
          <Typography variant="caption" sx={{ color: cx.green }}>
            Value satisfies the schema.
          </Typography>
        )}
      </Box>

      <TextField
        size="small"
        fullWidth
        label={requireReason ? 'Reason (required)' : 'Reason (optional)'}
        placeholder="Why is this config being added?"
        value={note}
        onChange={(event) => setNote(event.target.value)}
      />

      <Stack direction="row" sx={{ gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={onCancel} disabled={isSaving} sx={{ color: cx.text.tertiary }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canCreate}
          onClick={() => onCreate({ key, value: valueText, schema, note: note.trim() || null })}
          sx={{ backgroundColor: cx.blue.main, '&:hover': { backgroundColor: cx.blue.dark } }}
        >
          {isSaving ? 'Creating…' : 'Create config'}
        </Button>
      </Stack>
    </Stack>
  );
};

export default CreateConfigForm;
